import { useState, useMemo, useCallback } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import appConfig from '../../../config/app-config';
import useFetch from '../../../hooks/useFetch';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import { collateralApi } from '../../../services/collateralApi';
import { formatCurrency } from './collateralUtils';

const CONVERT_XML_API = appConfig.apis.convertXmlApiEnv;
const PERSON_API = appConfig.apis.personApiEnv;

const CNPJ_REGEX = /^[A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?[0-9]{2}$/i;

interface OperationOption {
    id: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    operationType: string;
    obligation: string;
    contractCode: string;
}

interface PersonResult {
    uuid: string;
    name: string;
    alias: string | null;
    documents: { type: string; number: string }[];
}

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

const LoadingOverlay = ({ message }: { message: string }) => (
    <div className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {message}
        </div>
    </div>
);

const NewAnalysisFromValidation = ({ onClose, onCreated }: Props) => {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selected, setSelected] = useState<OperationOption | null>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [operationCode, setOperationCode] = useState('');
    const [assignorName, setAssignorName] = useState('');
    const [assignorTaxId, setAssignorTaxId] = useState('');
    const [operationType, setOperationType] = useState('CESSAO');
    const [obligationType, setObligationType] = useState('SEM_REGRESSO');

    const [personResults, setPersonResults] = useState<PersonResult[]>([]);
    const [isSearchingPerson, setIsSearchingPerson] = useState(false);
    const [showPersonDropdown, setShowPersonDropdown] = useState(false);

    const [isCreating, setIsCreating] = useState(false);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: CONVERT_XML_API.key || '' },
        { key: 'Accept-Charset', value: 'utf-8' },
    ], []);
    const { isLoading, data } = useFetch(`${CONVERT_XML_API.address}/operations?page=0&pageSize=100`, fetchHeaders);

    const operations: OperationOption[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list.filter((o: OperationOption & Record<string, unknown>) => !o['sentToValidation'] && !o['collateralAnalysisId']);
    }, [data]);

    const filteredOps = useMemo(() => {
        let ops = operations;
        if (statusFilter) {
            ops = ops.filter(o => (statusFilter === 'closed') === (o.status === 'closed'));
        }
        if (search) {
            const s = search.toLowerCase();
            ops = ops.filter(o =>
                o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s)
            );
        }
        return ops;
    }, [operations, search, statusFilter]);

    const handleSelectOperation = (op: OperationOption) => {
        setSelected(op);
        setOperationCode(op.status === 'closed' && op.contractCode ? op.contractCode : op.operationCode || '');
        setAssignorName(op.assignorName || '');
        setAssignorTaxId(op.assignorCnpj || '');
        setOperationType(op.operationType || 'CESSAO');
        setObligationType(op.obligation || 'SEM_REGRESSO');
    };

    const clearPersonSearch = useCallback(() => {
        setPersonResults([]);
        setShowPersonDropdown(false);
    }, []);

    const searchPerson = useCallback(async (query: string) => {
        if (query.length < 3) { clearPersonSearch(); return; }
        setIsSearchingPerson(true);
        try {
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();
            const res = await fetch(`${PERSON_API.address}/person/search/legal_person/${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': PERSON_API.key || '' },
            });
            if (res.ok) {
                const result = await res.json();
                setPersonResults(result.results || []);
                setShowPersonDropdown(true);
            } else {
                clearPersonSearch();
            }
        } catch (_error: unknown) {
            clearPersonSearch();
        } finally {
            setIsSearchingPerson(false);
        }
    }, [clearPersonSearch]);

    const handlePersonSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            searchPerson(assignorName);
        }
    };

    const handleSelectPerson = (person: PersonResult) => {
        setAssignorName(person.name);
        const cnpj = person.documents?.find(d => d.type === 'CNPJ' && CNPJ_REGEX.test(d.number.trim()));
        if (cnpj) setAssignorTaxId(cnpj.number.replace(/[.\-/\s]/g, '').toUpperCase());
        setShowPersonDropdown(false);
    };

    const handleCreate = async () => {
        if (!selected) return;
        setIsCreating(true);
        try {
            await collateralApi.createAnalysis({
                offer_operation_id: selected.id,
                offer_operation_code: operationCode,
                offer_operation_type: operationType,
                obligation_type: obligationType,
                assignor_name: assignorName,
                assignor_tax_id: assignorTaxId,
                validation_config: {
                    validation_xml: 'DO_NOT_VALIDATE',
                    validation_invoice_pdf: 'DO_NOT_VALIDATE',
                    validation_receipt_stub: 'DO_NOT_VALIDATE',
                    validation_contact: 'PRE_DISBURSEMENT',
                    validation_sefaz: 'DO_NOT_VALIDATE',
                    validation_accepted_bill: 'DO_NOT_VALIDATE',
                    validation_purchase_contract: 'DO_NOT_VALIDATE',
                },
                total_documents: 0,
                total_receivables: 0,
                total_amount: 0,
            });
            onCreated();
        } catch (error: unknown) {
            const apiError = error as { status?: number };
            if (apiError?.status === 409) toast.warn('Análise já existe para esta operação.');
            else toast.error('Erro ao criar análise. Tente novamente.');
        } finally { setIsCreating(false); }
    };

    if (step === 'select') {
        return (
            <div className="flex flex-col h-full relative">
                {isLoading && <LoadingOverlay message="Carregando operações..." />}
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nova Análise de Validação</h3>
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm">Cancelar</button>
                        <button onClick={() => selected && setStep('configure')} disabled={!selected}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">Próximo</button>
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                        <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
                        <input type="text" placeholder="Buscar por código ou cedente..." value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                    </div>
                    <div className="flex rounded-md border border-gray-300 dark:border-slate-600 overflow-hidden text-xs">
                        {(['', 'closed', 'completed'] as const).map(s => (
                            <button key={s} onClick={() => setStatusFilter(s)}
                                className={`px-3 py-2 transition-colors ${statusFilter === s
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'}`}>
                                {s === '' ? 'Todos' : s === 'closed' ? 'Fechado' : 'Aberto'}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-auto space-y-2">
                    {filteredOps.length === 0 && !isLoading ? (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhuma operação disponível</div>
                    ) : filteredOps.map(op => (
                        <div key={op.id} onClick={() => handleSelectOperation(op)}
                            className={`p-3 rounded-lg border transition-colors cursor-pointer ${selected?.id === op.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                                : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm dark:text-white">{op.operationCode}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${op.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {op.status === 'closed' ? 'Fechado' : 'Aberto'}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{op.assignorName}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium dark:text-white">{formatCurrency(op.totalAmount || 0)}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{op.totalReceivables || 0} títulos · {op.totalDocuments || 0} docs</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            {isCreating && <LoadingOverlay message="Aguarde, criando análise..." />}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configurar Análise</h3>
                <div className="flex items-center gap-3">
                    <button onClick={() => setStep('select')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm">Voltar</button>
                    <button onClick={handleCreate} disabled={isCreating || !assignorName || !assignorTaxId}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">
                        {isCreating ? 'Criando...' : 'Criar Análise'}
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
                    <div className="text-gray-500 dark:text-gray-400">{selected!.operationCode} — {formatCurrency(selected!.totalAmount || 0)} · {selected!.totalReceivables || 0} títulos</div>
                </div>

                <TextEdit label="Código do lote" value={operationCode} onChange={e => setOperationCode(e.target.value)} />

                <div className="relative">
                    <TextEdit label="Nome do cedente (Enter para buscar)" value={assignorName} maxLength={180}
                        onChange={e => setAssignorName(e.target.value)}
                        onKeyDown={handlePersonSearchKeyDown} />
                    {isSearchingPerson && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showPersonDropdown && personResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {personResults.slice(0, 6).map(p => {
                                const cnpj = p.documents?.find(d => d.type === 'CNPJ' && CNPJ_REGEX.test(d.number.trim()))?.number || '';
                                return (
                                    <div key={p.uuid} onClick={() => handleSelectPerson(p)}
                                        className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                        <span className="font-medium dark:text-white">{p.name}</span>
                                        {cnpj && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{cnpj}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <TextEdit label="CNPJ do cedente (Enter para buscar)" value={assignorTaxId} maxLength={180}
                    onChange={e => setAssignorTaxId(e.target.value)}
                    onKeyDown={async (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); await searchPerson(assignorTaxId); } }} />

                <div className="grid grid-cols-2 gap-3">
                    <SelectTextField
                        label="Tipo Operação"
                        value={operationType}
                        onChange={e => setOperationType(e.target.value)}
                        items={[
                            { value: 'CESSAO', label: 'Cessão' },
                            { value: 'CPRF', label: 'CPRF' },
                        ]}
                        emptyLabel="Selecione..."
                    />
                    <SelectTextField
                        label="Obrigação"
                        value={obligationType}
                        onChange={e => setObligationType(e.target.value)}
                        items={[
                            { value: 'SEM_REGRESSO', label: 'Sem Regresso' },
                            { value: 'COM_REGRESSO', label: 'Com Regresso' },
                        ]}
                        emptyLabel="Selecione..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <TextEdit label="Total recebíveis" value={String(selected!.totalReceivables || 0)} disabled />
                    <TextEdit label="Valor total" value={formatCurrency(selected!.totalAmount || 0)} disabled />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-300">
                    Validação de contato (email/telefone) será habilitada automaticamente. Após criar, carregue os recebíveis pela listagem.
                </div>
            </div>
        </div>
    );
};

export default NewAnalysisFromValidation;
